import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Copy, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { PhoneNumberInput } from '@/components/ui/PhoneNumberInput';
import { Label } from '@/components/ui/Label';
import { Skeleton } from '@/components/ui/Skeleton';
import { Textarea } from '@/components/ui/Textarea';
import { useAuth } from '@/app/AuthProvider';
import {
  useCompanyProfile,
  useSaveCompanyProfile,
  type CompanyProfile,
  type CompanyProfileInput,
} from '@/features/admin/hooks';
import { formatStreetAddress, friendlyAdminError } from '@/features/admin/model';
import { formatDateOnly } from '@/lib/formatters';
import { canWrite } from '@/lib/permissions';

const EMPTY: CompanyProfileInput = {
  legalName: '',
  dba: '',
  ein: '',
  entityType: '',
  stateOfIncorporation: '',
  formationDate: '',
  website: '',
  phone: '',
  email: '',
  mailingLine1: '',
  mailingLine2: '',
  mailingCity: '',
  mailingState: '',
  mailingZip: '',
  physicalLine1: '',
  physicalLine2: '',
  physicalCity: '',
  physicalState: '',
  physicalZip: '',
  notes: '',
};

function fromProfile(profile: CompanyProfile | null): CompanyProfileInput {
  if (!profile) return EMPTY;
  return {
    legalName: profile.legal_name ?? '',
    dba: profile.dba ?? '',
    ein: profile.ein ?? '',
    entityType: profile.entity_type ?? '',
    stateOfIncorporation: profile.state_of_incorporation ?? '',
    formationDate: profile.formation_date ?? '',
    website: profile.website ?? '',
    phone: profile.phone ?? '',
    email: profile.email ?? '',
    mailingLine1: profile.mailing_line1 ?? '',
    mailingLine2: profile.mailing_line2 ?? '',
    mailingCity: profile.mailing_city ?? '',
    mailingState: profile.mailing_state ?? '',
    mailingZip: profile.mailing_zip ?? '',
    physicalLine1: profile.physical_line1 ?? '',
    physicalLine2: profile.physical_line2 ?? '',
    physicalCity: profile.physical_city ?? '',
    physicalState: profile.physical_state ?? '',
    physicalZip: profile.physical_zip ?? '',
    notes: profile.notes ?? '',
  };
}

function addressesMatch(profile: CompanyProfile | null): boolean {
  if (!profile) return true;
  const mailing = [
    profile.mailing_line1,
    profile.mailing_line2,
    profile.mailing_city,
    profile.mailing_state,
    profile.mailing_zip,
  ].join('|');
  const physical = [
    profile.physical_line1,
    profile.physical_line2,
    profile.physical_city,
    profile.physical_state,
    profile.physical_zip,
  ].join('|');
  return mailing === physical;
}

export function CompanyProfilePage() {
  const { role } = useAuth();
  const canEdit = canWrite(role, 'company_admin');
  const profileQuery = useCompanyProfile();
  const save = useSaveCompanyProfile();
  const [form, setForm] = useState<CompanyProfileInput>(EMPTY);
  const [sameAddress, setSameAddress] = useState(true);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (!profileQuery.isSuccess || hydrated) return;
    setForm(fromProfile(profileQuery.data));
    setSameAddress(addressesMatch(profileQuery.data));
    setHydrated(true);
  }, [hydrated, profileQuery.data, profileQuery.isSuccess]);

  const saved = profileQuery.data;
  const mailing = saved
    ? formatStreetAddress({
        line1: saved.mailing_line1,
        line2: saved.mailing_line2,
        city: saved.mailing_city,
        state: saved.mailing_state,
        zip: saved.mailing_zip,
      })
    : '';

  function set<K extends keyof CompanyProfileInput>(key: K, value: CompanyProfileInput[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const payload = sameAddress
      ? {
          ...form,
          physicalLine1: form.mailingLine1,
          physicalLine2: form.mailingLine2,
          physicalCity: form.mailingCity,
          physicalState: form.mailingState,
          physicalZip: form.mailingZip,
        }
      : form;
    try {
      await save.mutateAsync(payload);
      toast.success('Company profile saved.');
    } catch (err) {
      toast.error(friendlyAdminError(err, 'Could not save the company profile.'));
    }
  }

  return (
    <form className="space-y-6" onSubmit={(event) => void handleSubmit(event)}>
      <PageHeader
        eyebrow="Admin"
        title="Company profile"
        actions={
          canEdit ? (
            <Button type="submit" disabled={save.isPending || profileQuery.isLoading}>
              {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Save
            </Button>
          ) : undefined
        }
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Fact label="Legal name" value={saved?.legal_name ?? ''} />
        <Fact label="EIN" value={saved?.ein ?? ''} />
        <Fact
          label="Formation date"
          value={saved?.formation_date ? formatDateOnly(saved.formation_date) : ''}
        />
        <Fact label="Mailing address" value={mailing} />
      </div>

      {profileQuery.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : profileQuery.isError ? (
        <p className="text-sm text-muted-foreground">Couldn’t load the company profile.</p>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Legal</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <Field label="Legal name" id="legal-name">
                <Input
                  id="legal-name"
                  value={form.legalName}
                  onChange={(event) => set('legalName', event.target.value)}
                  disabled={!canEdit}
                />
              </Field>
              <Field label="DBA" id="dba">
                <Input
                  id="dba"
                  value={form.dba}
                  onChange={(event) => set('dba', event.target.value)}
                  disabled={!canEdit}
                />
              </Field>
              <Field label="EIN" id="ein">
                <Input
                  id="ein"
                  value={form.ein}
                  onChange={(event) => set('ein', event.target.value)}
                  disabled={!canEdit}
                  placeholder="00-0000000"
                />
              </Field>
              <Field label="Entity type" id="entity-type">
                <Input
                  id="entity-type"
                  value={form.entityType}
                  onChange={(event) => set('entityType', event.target.value)}
                  disabled={!canEdit}
                  placeholder="LLC, C-corp, …"
                />
              </Field>
              <Field label="State of incorporation" id="state">
                <Input
                  id="state"
                  value={form.stateOfIncorporation}
                  onChange={(event) => set('stateOfIncorporation', event.target.value)}
                  disabled={!canEdit}
                />
              </Field>
              <Field label="Formation date" id="formation-date">
                <Input
                  id="formation-date"
                  type="date"
                  value={form.formationDate}
                  onChange={(event) => set('formationDate', event.target.value)}
                  disabled={!canEdit}
                />
              </Field>
              <Field label="Website" id="website">
                <Input
                  id="website"
                  value={form.website}
                  onChange={(event) => set('website', event.target.value)}
                  disabled={!canEdit}
                />
              </Field>
              <Field label="Phone" id="phone">
                <PhoneNumberInput
                  id="phone"
                  value={form.phone}
                  onChange={(value) => set('phone', value)}
                  disabled={!canEdit}
                />
              </Field>
              <Field label="Email" id="email">
                <Input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={(event) => set('email', event.target.value)}
                  disabled={!canEdit}
                />
              </Field>
            </CardContent>
          </Card>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Mailing address</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-4">
                <AddressFields
                  prefix="mailing"
                  disabled={!canEdit}
                  line1={form.mailingLine1}
                  line2={form.mailingLine2}
                  city={form.mailingCity}
                  state={form.mailingState}
                  zip={form.mailingZip}
                  onChange={(key, value) => set(key, value)}
                />
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex-row items-center justify-between space-y-0">
                <CardTitle className="text-base">Physical address</CardTitle>
                {canEdit && (
                  <label className="flex items-center gap-2 text-xs text-muted-foreground">
                    <input
                      type="checkbox"
                      checked={sameAddress}
                      onChange={(event) => setSameAddress(event.target.checked)}
                    />
                    Same as mailing
                  </label>
                )}
              </CardHeader>
              <CardContent className="grid gap-4">
                <AddressFields
                  prefix="physical"
                  disabled={!canEdit || sameAddress}
                  line1={sameAddress ? form.mailingLine1 : form.physicalLine1}
                  line2={sameAddress ? form.mailingLine2 : form.physicalLine2}
                  city={sameAddress ? form.mailingCity : form.physicalCity}
                  state={sameAddress ? form.mailingState : form.physicalState}
                  zip={sameAddress ? form.mailingZip : form.physicalZip}
                  onChange={(key, value) => set(key, value)}
                />
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={form.notes}
                onChange={(event) => set('notes', event.target.value)}
                disabled={!canEdit}
                placeholder="Wire instructions live in Financials. Use this for the non-banking facts people ask for — registered agent, DUNS, resale certificate number."
              />
            </CardContent>
          </Card>
        </>
      )}
    </form>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  const text = value.trim();
  return (
    <div className="rounded-lg border border-border bg-card px-4 py-3">
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
        {text && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            aria-label={`Copy ${label}`}
            onClick={() => {
              void navigator.clipboard.writeText(text).then(
                () => toast.success(`Copied ${label.toLowerCase()}.`),
                () => toast.error('Could not copy.'),
              );
            }}
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      <p className="mt-1 whitespace-pre-line text-sm text-foreground">
        {text || 'Not on file yet'}
      </p>
    </div>
  );
}

function Field({ label, id, children }: { label: string; id: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  );
}

function AddressFields({
  prefix,
  disabled,
  line1,
  line2,
  city,
  state,
  zip,
  onChange,
}: {
  prefix: 'mailing' | 'physical';
  disabled: boolean;
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip: string;
  onChange: (
    key:
      | 'mailingLine1'
      | 'mailingLine2'
      | 'mailingCity'
      | 'mailingState'
      | 'mailingZip'
      | 'physicalLine1'
      | 'physicalLine2'
      | 'physicalCity'
      | 'physicalState'
      | 'physicalZip',
    value: string,
  ) => void;
}) {
  const line1Key = prefix === 'mailing' ? 'mailingLine1' : 'physicalLine1';
  const line2Key = prefix === 'mailing' ? 'mailingLine2' : 'physicalLine2';
  const cityKey = prefix === 'mailing' ? 'mailingCity' : 'physicalCity';
  const stateKey = prefix === 'mailing' ? 'mailingState' : 'physicalState';
  const zipKey = prefix === 'mailing' ? 'mailingZip' : 'physicalZip';
  return (
    <>
      <Input
        aria-label="Address line 1"
        value={line1}
        disabled={disabled}
        placeholder="Street address"
        onChange={(event) => onChange(line1Key, event.target.value)}
      />
      <Input
        aria-label="Address line 2"
        value={line2}
        disabled={disabled}
        placeholder="Suite, unit"
        onChange={(event) => onChange(line2Key, event.target.value)}
      />
      <div className="grid grid-cols-6 gap-2">
        <Input
          aria-label="City"
          className="col-span-3"
          value={city}
          disabled={disabled}
          placeholder="City"
          onChange={(event) => onChange(cityKey, event.target.value)}
        />
        <Input
          aria-label="State"
          className="col-span-1"
          value={state}
          disabled={disabled}
          placeholder="ST"
          onChange={(event) => onChange(stateKey, event.target.value)}
        />
        <Input
          aria-label="ZIP"
          className="col-span-2"
          value={zip}
          disabled={disabled}
          placeholder="ZIP"
          onChange={(event) => onChange(zipKey, event.target.value)}
        />
      </div>
    </>
  );
}
